import { useState, useMemo } from 'react'
import { Search, Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useToast } from '@/hooks/use-toast'
import { useKnowledgeBases, KnowledgeBase } from '@/hooks/useKnowledgeBases'
import { useAddKnowledgeBases } from '@/hooks/useAssistants'
import { formatDistanceToNow } from 'date-fns'

interface AddKnowledgeBasesModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  assistantId: string
  existingKBIds: string[]
}

export function AddKnowledgeBasesModal({
  open,
  onOpenChange,
  assistantId,
  existingKBIds,
}: AddKnowledgeBasesModalProps) {
  const { toast } = useToast()
  const { data: kbData, isLoading: kbsLoading } = useKnowledgeBases({ page: 1, pageSize: 100, search: '' })
  const allKBs = kbData?.items
  const addKBs = useAddKnowledgeBases()

  const [searchQuery, setSearchQuery] = useState('')
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  // Filter out already connected KBs and apply search
  const availableKBs = useMemo(() => {
    if (!allKBs) return []
    
    const filtered = allKBs.filter((kb) => !existingKBIds.includes(kb.id))
    
    if (!searchQuery.trim()) return filtered
    
    const query = searchQuery.toLowerCase()
    return filtered.filter((kb) => kb.name.toLowerCase().includes(query))
  }, [allKBs, existingKBIds, searchQuery])

  const handleToggle = (kbId: string) => {
    setSelectedIds((prev) =>
      prev.includes(kbId) ? prev.filter((id) => id !== kbId) : [...prev, kbId]
    )
  }

  const handleAdd = async () => {
    if (selectedIds.length === 0) return

    try {
      await addKBs.mutateAsync({ assistantId, kbIds: selectedIds })
      toast({
        title: 'Knowledge bases added',
        description: `Added ${selectedIds.length} knowledge base(s) to the assistant.`,
      })
      setSelectedIds([])
      setSearchQuery('')
      onOpenChange(false)
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to add knowledge bases',
        variant: 'destructive',
      })
    }
  }

  const handleClose = () => {
    setSelectedIds([])
    setSearchQuery('')
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Knowledge Bases</DialogTitle>
          <DialogDescription>
            Select knowledge bases to connect to this assistant.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search knowledge bases..."
              className="pl-10"
            />
          </div>

          {/* KB List */}
          {kbsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : availableKBs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {existingKBIds.length === (allKBs?.length ?? 0)
                ? 'All knowledge bases are already connected'
                : 'No knowledge bases found'}
            </div>
          ) : (
            <ScrollArea className="h-[300px]">
              <div className="space-y-2">
                {availableKBs.map((kb) => (
                  <KBListItem
                    key={kb.id}
                    kb={kb}
                    isSelected={selectedIds.includes(kb.id)}
                    onToggle={() => handleToggle(kb.id)}
                  />
                ))}
              </div>
            </ScrollArea>
          )}

          {/* Selection count */}
          <div className="text-sm text-muted-foreground">
            {selectedIds.length} knowledge base{selectedIds.length !== 1 ? 's' : ''} selected
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleAdd} 
            disabled={selectedIds.length === 0 || addKBs.isPending}
          >
            {addKBs.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Adding...
              </>
            ) : (
              `Add Selected (${selectedIds.length})`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface KBListItemProps {
  kb: KnowledgeBase
  isSelected: boolean
  onToggle: () => void
}

function KBListItem({ kb, isSelected, onToggle }: KBListItemProps) {
  return (
    <div
      onClick={onToggle}
      className={`
        flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors
        ${isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-muted-foreground/50'}
      `}
    >
      <Checkbox
        checked={isSelected}
        onCheckedChange={onToggle}
        className="mt-0.5"
      />
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{kb.name}</div>
        <div className="text-xs text-muted-foreground mt-1">
          {kb.document_count} doc{kb.document_count !== 1 ? 's' : ''} •{' '}
          Last updated {formatDistanceToNow(new Date(kb.updated_at), { addSuffix: false })} ago
        </div>
      </div>
    </div>
  )
}
